import {
  Badge,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import CustomModal from 'components/modal/Modal';
import FormField from 'components/ui/FormField';
import Cie10Combobox from 'components/ui/Cie10Combobox';
import { useUpdateEpisode } from 'hooks/useEpisodes';
import React, { useEffect, useState } from 'react';
import { MdAutoAwesome, MdLocalHospital } from 'react-icons/md';
import { ClinicalEpisode } from 'types/models';

const CIE10_REGEX = /^[A-Z][0-9]{2}(\.[0-9]{1,2})?$/;

interface EpisodeDiagnosticoModalProps {
  isOpen: boolean;
  onClose: () => void;
  episode: ClinicalEpisode;
}

export default function EpisodeDiagnosticoModal({
  isOpen,
  onClose,
  episode,
}: EpisodeDiagnosticoModalProps) {
  const toast = useToast();
  const updateEpisode = useUpdateEpisode();

  const textColor    = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor   = useColorModeValue('secondaryGray.500', 'secondaryGray.400');
  const sectionColor = useColorModeValue('brand.500', 'brand.400');
  const contextBg    = useColorModeValue('gray.50', 'navy.700');
  const contextBorder = useColorModeValue('gray.200', 'whiteAlpha.200');

  const [diagnostico, setDiagnostico] = useState('');
  const [cie10, setCie10]             = useState('');
  const [diagnosticoSec, setDiagnosticoSec] = useState('');
  const [cie10Error, setCie10Error]   = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDiagnostico(episode.diagnosticoPrincipal ?? '');
      setCie10(episode.codigoCie10 ?? '');
      setDiagnosticoSec(episode.diagnosticoSecundario ?? '');
      setCie10Error('');
    }
  }, [isOpen, episode]);

  const handleSubmit = async () => {
    if (cie10.trim() && !CIE10_REGEX.test(cie10.trim().toUpperCase())) {
      setCie10Error('Formato inválido. Ej: M51.1 o A00');
      return;
    }
    setCie10Error('');

    setIsSubmitting(true);
    try {
      await updateEpisode.mutateAsync({
        patientId: episode.pacienteId,
        episodeId: episode.id,
        payload: {
          diagnosticoPrincipal:  diagnostico.trim() || undefined,
          codigoCie10:           cie10.trim().toUpperCase() || undefined,
          diagnosticoSecundario: diagnosticoSec.trim() || undefined,
        },
      });
      toast({
        title: 'Diagnóstico guardado',
        status: 'success',
        duration: 2500,
        isClosable: true,
        position: 'top-right',
      });
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        status === 422
          ? 'No se puede editar un episodio cerrado o archivado'
          : err?.response?.data?.message ?? 'Error al guardar diagnóstico';
      toast({
        title: 'Error',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = (
    <Flex align='center' gap='3'>
      <Flex
        w='36px' h='36px'
        bg='teal.500'
        borderRadius='10px'
        align='center' justify='center'
        flexShrink={0}>
        <Icon as={MdLocalHospital} color='white' w='18px' h='18px' />
      </Flex>
      <Flex direction='column'>
        <Text color={textColor} fontSize='md' fontWeight='800' lineHeight='1.2'>
          Diagnóstico
        </Text>
        <Text color='secondaryGray.600' fontSize='xs' fontWeight='400'>
          {episode.codigoHc}
        </Text>
      </Flex>
    </Flex>
  );

  const iaButton = (
    <Tooltip label='Próximamente: sugerencia de diagnóstico con IA' placement='left' hasArrow>
      <Flex
        align='center' gap='6px'
        px='10px' py='6px'
        borderRadius='10px'
        border='1px dashed'
        borderColor='purple.300'
        cursor='not-allowed'
        opacity={0.5}
        userSelect='none'>
        <Icon as={MdAutoAwesome} color='purple.400' w='14px' h='14px' />
        <Text fontSize='xs' fontWeight='700' color='purple.500'>Sugerir con IA</Text>
        <Badge colorScheme='purple' fontSize='9px' borderRadius='full' px='5px'>pronto</Badge>
      </Flex>
    </Tooltip>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      size='lg'
      scrollBehavior='inside'
      title={modalTitle}
      headerExtra={iaButton}
      primaryBtnLabel='Guardar diagnóstico'
      onPrimaryClick={handleSubmit}
      primaryBtnLoading={isSubmitting}
    >
      {/* Contexto del episodio */}
      <Box
        bg={contextBg}
        border='1px solid'
        borderColor={contextBorder}
        borderRadius='12px'
        px='14px' py='10px'
        mb='20px'>
        <Text
          fontSize='9px' fontWeight='700' color={mutedColor}
          textTransform='uppercase' letterSpacing='0.08em' mb='4px'>
          Motivo de consulta
        </Text>
        <Text fontSize='sm' color={textColor} fontWeight='500' noOfLines={3}>
          {episode.motivoConsulta}
        </Text>
      </Box>

      <Text
        color={sectionColor} fontSize='xs' fontWeight='800'
        textTransform='uppercase' letterSpacing='wider' mb='12px'>
        Diagnóstico fisioterapéutico
      </Text>

      {/* CIE-10 — campo principal de búsqueda */}
      <FormControl isInvalid={!!cie10Error} mb='16px'>
        <Text fontSize='sm' fontWeight='700' color={textColor} mb='6px'>
          Código CIE-10
          <Text as='span' fontSize='xs' fontWeight='400' color={mutedColor} ms='6px'>
            — busca por código o nombre de enfermedad
          </Text>
        </Text>
        <Cie10Combobox
          value={cie10}
          size='md'
          placeholder='Ej: lumbalgia, M54.5, cervicalgia…'
          onChange={(code) => { setCie10(code); if (cie10Error) setCie10Error(''); }}
          onSelect={(entry) => {
            setCie10(entry.code);
            setDiagnostico(entry.desc);
            setCie10Error('');
          }}
        />
        {cie10Error && <FormErrorMessage ms='10px'>{cie10Error}</FormErrorMessage>}
      </FormControl>

      <FormField
        id='diag-principal'
        label='Diagnóstico principal'
        placeholder='Se rellena al seleccionar CIE-10 — editable'
        mb='16px'
        value={diagnostico}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDiagnostico(e.target.value)}
      />

      <FormField
        id='diag-secundario'
        label='Diagnóstico secundario'
        placeholder='Opcional — comorbilidades o hallazgos adicionales'
        mb='0'
        value={diagnosticoSec}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDiagnosticoSec(e.target.value)}
      />
    </CustomModal>
  );
}
